import { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Compass, Loader2 } from "lucide-react";
import axios from "axios";
import StoryViewer from "./StoryViewer";
import StoryCreator from "./StoryCreator";

const API = process.env.REACT_APP_BACKEND_URL;

const StoryBar = ({ user, onStoryCreated, isDark = false }) => {
  const [connectionStories, setConnectionStories] = useState([]);
  const [discoverStories, setDiscoverStories] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [showViewer, setShowViewer] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUserIndex, setSelectedUserIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewerMode, setViewerMode] = useState("connections"); // "connections" veya "discover"

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      setLoading(true);
      const [connectionsRes, discoverRes, myStoriesRes] = await Promise.all([
        axios.get(`${API}/api/stories`),
        axios.get(`${API}/api/stories/discover`),
        axios.get(`${API}/api/stories/my`)
      ]);
      
      setConnectionStories(connectionsRes.data.users || []);
      setDiscoverStories(discoverRes.data.users || []);
      setMyStories(myStoriesRes.data.stories || []);
    } catch (error) {
      console.error("Stories fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMyStory = () => {
    if (myStories.length > 0) {
      // Kendi hikayelerimi göster
      const myUserData = {
        userId: user.id,
        userName: user.name,
        userAvatar: user.profileImage,
        stories: myStories,
        hasUnviewed: false
      };
      setSelectedUsers([myUserData]);
      setSelectedUserIndex(0);
      setViewerMode("my");
      setShowViewer(true);
    }
  };

  const handleOpenConnectionStory = (index) => {
    setSelectedUsers(connectionStories);
    setSelectedUserIndex(index);
    setViewerMode("connections");
    setShowViewer(true);
  };

  const handleOpenDiscoverStory = (index) => {
    setSelectedUsers(discoverStories);
    setSelectedUserIndex(index);
    setViewerMode("discover");
    setShowViewer(true);
  };

  const handleStoryCreated = (story) => {
    fetchStories();
    onStoryCreated?.(story);
  };

  const hasMyStory = myStories.length > 0;
  const hasConnectionStories = connectionStories.length > 0;
  const hasDiscoverStories = discoverStories.length > 0;

  if (loading) {
    return (
      <div className={`rounded-xl shadow-sm p-4 mb-4 ${isDark ? 'bg-[#2a2d2f]' : 'bg-white'}`} data-testid="story-bar">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`rounded-xl shadow-sm p-4 mb-4 transition-colors duration-300 ${isDark ? 'bg-[#2a2d2f]' : 'bg-white'}`} data-testid="story-bar">
        {/* Üst Satır: Kendi hikayem + Bağlantıların hikayeleri */}
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {/* Kendi hikayem */}
          <div className="flex flex-col items-center flex-shrink-0">
            <button
              onClick={hasMyStory ? handleOpenMyStory : () => setShowCreator(true)}
              className="relative"
              data-testid="my-story-button"
            >
              <Avatar className={`h-16 w-16 ${hasMyStory ? 'ring-2 ring-emerald-500' : `ring-2 ${isDark ? 'ring-gray-500' : 'ring-gray-200'}`}`}>
                <AvatarImage src={user?.profileImage} />
                <AvatarFallback className={`${isDark ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                  {user?.name?.[0]}
                </AvatarFallback>
              </Avatar>
              {/* + butonu her zaman göster (hikaye varsa veya yoksa) */}
              <div className="absolute -bottom-1 -right-1 bg-emerald-600 rounded-full p-1 cursor-pointer hover:bg-emerald-700 transition-colors">
                <Plus className="h-3 w-3 text-white" />
              </div>
            </button>
            <span className={`text-xs mt-1 truncate w-16 text-center ${isDark ? 'text-white' : 'text-gray-600'}`}>
              {hasMyStory ? "Hikayen" : "Hikaye Ekle"}
            </span>
            {hasMyStory && (
              <span className={`text-xs font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{myStories.length}</span>
            )}
          </div>

          {/* Yeni hikaye ekle butonu (+ işareti ayrı olarak) */}
          {hasMyStory && (
            <div className="flex flex-col items-center flex-shrink-0">
              <button
                onClick={() => setShowCreator(true)}
                className={`h-16 w-16 rounded-full border-2 border-dashed flex items-center justify-center transition-colors ${isDark ? 'border-emerald-500 hover:border-emerald-400 hover:bg-emerald-900/30' : 'border-emerald-400 hover:border-emerald-600 hover:bg-emerald-50'}`}
                data-testid="add-story-button"
              >
                <Plus className={`h-6 w-6 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
              </button>
              <span className={`text-xs mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Yeni Ekle</span>
            </div>
          )}

          {/* Ayırıcı çizgi */}
          {hasConnectionStories && (
            <div className="flex items-center px-2">
              <div className={`h-12 w-px ${isDark ? 'bg-gray-500' : 'bg-gray-200'}`}></div>
            </div>
          )}

          {/* Bağlantıların hikayeleri */}
          {connectionStories.map((storyUser, index) => (
            <div key={storyUser.userId} className="flex flex-col items-center flex-shrink-0">
              <button
                onClick={() => handleOpenConnectionStory(index)}
                className="relative"
                data-testid={`connection-story-${index}`}
              >
                <Avatar className={`h-16 w-16 ring-2 ${storyUser.hasUnviewed ? 'ring-emerald-500' : isDark ? 'ring-gray-500' : 'ring-gray-300'}`}>
                  <AvatarImage src={storyUser.userAvatar} />
                  <AvatarFallback className={isDark ? 'bg-gray-700' : 'bg-gray-100'}>
                    {storyUser.userName?.[0]}
                  </AvatarFallback>
                </Avatar>
                {storyUser.stories.length > 1 && (
                  <div className="absolute -bottom-1 -right-1 bg-gray-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {storyUser.stories.length}
                  </div>
                )}
              </button>
              <span className={`text-xs mt-1 truncate w-16 text-center ${isDark ? 'text-white' : 'text-gray-600'}`}>
                {storyUser.userName?.split(" ")[0]}
              </span>
            </div>
          ))}

          {/* Boş durum - hiç bağlantı hikayesi yoksa */}
          {!hasConnectionStories && !hasMyStory && (
            <div className="flex flex-col items-center flex-shrink-0">
              <button
                onClick={() => setShowCreator(true)}
                className={`h-16 w-16 rounded-full border-2 border-dashed flex items-center justify-center transition-colors ${isDark ? 'border-gray-500 hover:border-emerald-500 hover:bg-emerald-900/30' : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'}`}
              >
                <Plus className={`h-6 w-6 ${isDark ? 'text-white' : 'text-gray-400'}`} />
              </button>
              <span className={`text-xs mt-1 ${isDark ? 'text-white' : 'text-gray-500'}`}>Hikaye Ekle</span>
            </div>
          )}
        </div>

        {/* Alt Satır: Keşfet Hikayeleri */}
        {hasDiscoverStories && (
          <>
            <div className={`border-t mt-3 pt-3 ${isDark ? 'border-gray-600' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <Compass className="h-4 w-4 text-orange-500" />
                <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-600'}`}>Keşfet</span>
                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>• Popüler hikayeler</span>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-1 scrollbar-hide">
                {discoverStories.map((storyUser, index) => (
                  <div key={storyUser.userId} className="flex flex-col items-center flex-shrink-0">
                    <button
                      onClick={() => handleOpenDiscoverStory(index)}
                      className="relative"
                      data-testid={`discover-story-${index}`}
                    >
                      <Avatar className={`h-14 w-14 ring-2 ${storyUser.hasUnviewed ? 'ring-orange-400' : isDark ? 'ring-gray-500' : 'ring-gray-200'}`}>
                        <AvatarImage src={storyUser.userAvatar} />
                        <AvatarFallback className={isDark ? 'bg-orange-900 text-orange-300' : 'bg-orange-50 text-orange-600'}>
                          {storyUser.userName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      {storyUser.totalViews > 0 && (
                        <div className="absolute -bottom-1 -right-1 bg-orange-500 text-white text-[10px] rounded-full px-1.5 py-0.5 flex items-center justify-center">
                          {storyUser.totalViews > 999 ? '999+' : storyUser.totalViews}
                        </div>
                      )}
                    </button>
                    <span className={`text-xs mt-1 truncate w-14 text-center ${isDark ? 'text-white' : 'text-gray-500'}`}>
                      {storyUser.userName?.split(" ")[0]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Story Viewer */}
      <StoryViewer
        open={showViewer}
        onClose={() => setShowViewer(false)}
        users={selectedUsers}
        initialUserIndex={selectedUserIndex}
        currentUserId={user?.id}
      />

      {/* Story Creator */}
      <StoryCreator
        open={showCreator}
        onClose={() => setShowCreator(false)}
        onStoryCreated={handleStoryCreated}
        user={user}
      />
    </>
  );
};

export default StoryBar;
